import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import type { MutableRefObject } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReportingArtifact } from 'src/agent/reporting/reportingPresentation.types'
import { liveReportingQueryKeys } from 'src/queries/liveReporting/liveReporting.keys'
import { liveReportingMutations } from 'src/queries/liveReporting/liveReporting.mutations'
import { liveReportingQueries } from 'src/queries/liveReporting/liveReporting.queries'

import {
  isReportingAccessDenied,
  newReportingIdempotencyKey,
  reportingErrorStatus,
} from './reportingConversation.errors'

interface UseReportingArtifactPinningOptions {
  actorId?: string
  organizationId?: string
  isAuthenticated: boolean
  accessDenied: boolean
  accessDeniedRef: MutableRefObject<boolean>
  denyAccess: (status: number) => void
}

export const useReportingArtifactPinning = ({
  actorId,
  organizationId,
  isAuthenticated,
  accessDenied,
  accessDeniedRef,
  denyAccess,
}: UseReportingArtifactPinningOptions) => {
  const queryClient = useQueryClient()
  const summariesQuery = useInfiniteQuery(
    liveReportingQueries.summaries({
      actorId,
      organizationId,
      reportingEnabled: isAuthenticated,
    }),
  )
  const pinIdempotencyKeysRef = useRef(new Map<string, string>())
  const pinnedReports = useMemo(
    () => summariesQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [summariesQuery.data?.pages],
  )
  const { fetchNextPage: fetchMorePinnedReports } = summariesQuery
  useEffect(() => {
    if (
      summariesQuery.hasNextPage &&
      !summariesQuery.isFetchingNextPage &&
      !summariesQuery.isFetchNextPageError
    ) {
      void fetchMorePinnedReports()
    }
  }, [
    fetchMorePinnedReports,
    summariesQuery.hasNextPage,
    summariesQuery.isFetchNextPageError,
    summariesQuery.isFetchingNextPage,
  ])
  const canPinArtifacts = Boolean(
    isAuthenticated &&
      !accessDenied &&
      summariesQuery.isSuccess &&
      summariesQuery.hasNextPage === false &&
      !summariesQuery.isFetching &&
      !summariesQuery.isError,
  )
  const pinContextByCandidateRef = useRef(
    new Map<
      string,
      { artifactId: string; actorId: string; organizationId: string }
    >(),
  )
  const scopeRef = useRef({ actorId, organizationId })
  const [optimisticPinnedIds, setOptimisticPinnedIds] = useState<
    ReadonlySet<string>
  >(() => new Set())
  const [pinningArtifactId, setPinningArtifactId] = useState<string>()
  const [pinUnavailableArtifactIds, setPinUnavailableArtifactIds] = useState<
    ReadonlySet<string>
  >(() => new Set())
  const pinMutation = useMutation({
    ...liveReportingMutations.pin(),
    onSuccess: (_result, variables) => {
      const context = pinContextByCandidateRef.current.get(
        variables.candidateId,
      )
      if (!context) return
      pinContextByCandidateRef.current.delete(variables.candidateId)
      setOptimisticPinnedIds(
        (current) =>
          new Set([...current, context.artifactId, variables.candidateId]),
      )
      setPinningArtifactId((current) =>
        current === context.artifactId ? undefined : current,
      )
      void queryClient.invalidateQueries({
        queryKey: liveReportingQueryKeys.summaries(
          context.actorId,
          context.organizationId,
        ),
      })
    },
    onError: (error, variables) => {
      const context = pinContextByCandidateRef.current.get(
        variables.candidateId,
      )
      if (!context) return
      setPinningArtifactId((current) =>
        current === context.artifactId ? undefined : current,
      )
      const status = reportingErrorStatus(error)
      if (status === 404 || status === 410) {
        pinContextByCandidateRef.current.delete(variables.candidateId)
        setPinUnavailableArtifactIds((current) =>
          current.has(context.artifactId)
            ? current
            : new Set([...current, context.artifactId]),
        )
      } else if (isReportingAccessDenied(error)) {
        pinContextByCandidateRef.current.delete(variables.candidateId)
        denyAccess(status ?? 403)
      }
    },
  })

  useEffect(() => {
    if (isReportingAccessDenied(summariesQuery.error)) {
      denyAccess(reportingErrorStatus(summariesQuery.error) ?? 403)
    }
  }, [denyAccess, summariesQuery.error])

  const pinnedArtifactIds = useMemo<ReadonlySet<string>>(
    () =>
      accessDenied
        ? new Set()
        : new Set([
            ...pinnedReports.map((report) => report.candidate_id),
            ...optimisticPinnedIds,
          ]),
    [accessDenied, optimisticPinnedIds, pinnedReports],
  )

  useEffect(() => {
    const previous = scopeRef.current
    const scopeChanged =
      previous.actorId !== actorId || previous.organizationId !== organizationId
    if (scopeChanged || !isAuthenticated || accessDenied) {
      pinIdempotencyKeysRef.current.clear()
      pinContextByCandidateRef.current.clear()
      setOptimisticPinnedIds(new Set())
      setPinningArtifactId(undefined)
      setPinUnavailableArtifactIds(new Set())
    }
    scopeRef.current = { actorId, organizationId }
  }, [accessDenied, actorId, isAuthenticated, organizationId])

  const pinArtifact = useCallback(
    (artifact: ReportingArtifact) => {
      const candidateId = artifact.liveReportCandidateId
      if (!candidateId) {
        setPinUnavailableArtifactIds((current) =>
          current.has(artifact.id)
            ? current
            : new Set([...current, artifact.id]),
        )
        return
      }
      if (
        !actorId ||
        !organizationId ||
        !isAuthenticated ||
        !canPinArtifacts ||
        accessDeniedRef.current ||
        pinningArtifactId !== undefined ||
        pinnedArtifactIds.has(artifact.id) ||
        pinnedArtifactIds.has(candidateId) ||
        pinUnavailableArtifactIds.has(artifact.id)
      ) {
        return
      }

      const idempotencyKey =
        pinIdempotencyKeysRef.current.get(artifact.id) ??
        newReportingIdempotencyKey()
      pinIdempotencyKeysRef.current.set(artifact.id, idempotencyKey)
      pinContextByCandidateRef.current.set(candidateId, {
        artifactId: artifact.id,
        actorId,
        organizationId,
      })
      setPinningArtifactId(artifact.id)
      pinMutation.mutate({
        candidateId,
        title: artifact.title,
        idempotencyKey,
      })
    },
    [
      accessDeniedRef,
      actorId,
      canPinArtifacts,
      denyAccess,
      isAuthenticated,
      organizationId,
      pinMutation,
      pinnedArtifactIds,
      pinningArtifactId,
      pinUnavailableArtifactIds,
    ],
  )

  return {
    pinArtifact,
    canPinArtifacts,
    pinnedArtifactIds,
    pinningArtifactId,
    pinUnavailableArtifactIds,
  }
}
